import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { getAllArticles, getCategories, getHotArticles, buildArticlePath, buildCategoryPath } from '@/utils/articles';
import type { Article } from '@/types';

const navItems = [
  { label: '首页', to: '/' },
  ...getCategories().map((category) => ({
    label: category.name,
    to: buildCategoryPath(category.slug),
  })),
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const hotTerms = useMemo(() => getHotArticles(6), []);

  const results: Article[] = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    if (!value) return [];
    return getAllArticles()
      .filter(
        (item) =>
          item.title.toLowerCase().includes(value) ||
          item.categoryName.includes(keyword.trim()) ||
          item.tags.some((tag) => tag.toLowerCase().includes(value)),
      )
      .slice(0, 6);
  }, [keyword]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAll = () => {
    setSearchOpen(false);
    setKeyword('');
    setMenuOpen(false);
  };

  const goToArticle = (id: string) => {
    closeAll();
    navigate(buildArticlePath(id));
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="w-full bg-foreground-950 text-background-50">
        <div className="mx-auto flex h-9 max-w-[1280px] items-center gap-4 px-4 md:px-6">
          <span className="hidden whitespace-nowrap text-xs text-background-300 sm:block">2026年9月12日 星期六</span>
          <span className="hidden h-3 w-px bg-background-700 sm:block" />
          <div className="marquee-mask relative flex-1 overflow-hidden">
            <div className="animate-ticker flex w-max items-center gap-8 whitespace-nowrap text-xs">
              {[...hotTerms, ...hotTerms].map((item, index) => (
                <Link
                  key={`${item.id}-${index}`}
                  to={buildArticlePath(item.id)}
                  className="flex items-center gap-2 transition-colors hover:text-accent-300"
                >
                  <i className="ri-fire-fill text-primary-400" />
                  {item.title.slice(0, 18)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between gap-4 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2.5" onClick={closeAll}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-base font-bold text-background-50">
              18
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold tracking-tight text-foreground-950">爆料网</span>
              <span className="mt-0.5 text-[10px] tracking-[0.25em] text-primary-600">每日追更</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div ref={searchRef} className="relative hidden md:block">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5">
                <i className="ri-search-line text-base text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(event) => {
                    setKeyword(event.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="搜索爆料关键词"
                  className="w-36 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:w-44"
                />
              </div>
              {searchOpen && keyword.trim() && (
                <div className="absolute right-0 top-11 w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-sm">
                  {results.length > 0 ? (
                    <ul>
                      {results.map((item) => (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => goToArticle(item.id)}
                            className="flex w-full flex-col items-start gap-1 border-b border-background-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-background-100"
                          >
                            <span className="line-clamp-1 text-sm font-medium text-foreground-900">{item.title}</span>
                            <span className="text-xs text-foreground-500">{item.categoryName}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-4 py-4 text-sm text-foreground-500">没有找到相关爆料，换个词试试</p>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
            >
              我要投稿
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((value) => !value)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden" aria-label="移动端导航">
            <ul className="flex flex-col">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={closeAll}
                    className={({ isActive }) =>
                      `block rounded-md px-3 py-2.5 text-sm font-medium ${
                        isActive ? 'bg-primary-50 text-primary-700' : 'text-foreground-800'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className="mt-2">
                <Link
                  to="/about"
                  onClick={closeAll}
                  className="block rounded-md bg-primary-500 px-3 py-2.5 text-center text-sm font-medium text-background-50"
                >
                  我要投稿
                </Link>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
}